/**
 * IPFSService - Multi-Gateway IPFS Fetcher
 *
 * Provides reliable IPFS content fetching with automatic fallback
 * across multiple gateway providers.
 *
 * Features:
 * - Multiple gateway support (Filebase, IPFS.io, dweb.link, Pinata)
 * - Automatic failover on timeout or error
 * - Configurable timeout per gateway
 * - Support for JSON and text content
 * - CID extraction from various URI formats
 */

class IPFSService {
  constructor() {
    // Gateway list in priority order - Filebase first for reliability
    this.gateways = [
      'https://ipfs.filebase.io/ipfs/',
      'https://ipfs.io/ipfs/',
      'https://dweb.link/ipfs/',
      'https://gateway.pinata.cloud/ipfs/',
      'https://cloudflare-ipfs.com/ipfs/',
      'https://freeweb3.infura-ipfs.io/ipfs/' // Legacy fallback
    ];

    this.timeout = 10000; // 10 seconds per gateway
    this.maxRetries = this.gateways.length;
  }

  /**
   * Extract CID from various IPFS URI formats
   * @param {string} uri - IPFS URI in any format
   * @returns {string} - Clean CID
   */
  extractCID(uri) {
    if (!uri) return '';

    // Handle ipfs:// protocol
    if (uri.startsWith('ipfs://')) {
      return uri.replace('ipfs://', '');
    }

    // Handle /ipfs/ path format
    const match = uri.match(/\/ipfs\/([a-zA-Z0-9]+)/);
    if (match) {
      return match[1];
    }

    // Handle bare CID (assume last 46 characters for v0 CIDs)
    // v0 CIDs start with Qm and are 46 characters
    // v1 CIDs start with b and are longer
    if (uri.startsWith('Qm') || uri.startsWith('b')) {
      return uri;
    }

    // Fallback: try to extract last 46 chars (v0 CID length)
    if (uri.length >= 46) {
      return uri.slice(-46);
    }

    return uri;
  }

  /**
   * Fetch content from IPFS with multi-gateway fallback
   * @param {string} uri - IPFS URI or CID
   * @param {object} options - Fetch options
   * @returns {Promise<Response>} - Fetch response
   */
  async fetchFromIPFS(uri, options = {}) {
    const cid = this.extractCID(uri);

    if (!cid) {
      throw new Error('Invalid IPFS URI or CID');
    }

    let lastError = null;

    // Try each gateway in sequence
    for (let i = 0; i < this.gateways.length; i++) {
      const gateway = this.gateways[i];

      try {
        console.log(`[IPFSService] Attempting gateway ${i + 1}/${this.gateways.length}: ${gateway}`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        const response = await fetch(`${gateway}${cid}`, {
          signal: controller.signal,
          ...options
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          console.log(`[IPFSService] Success with gateway: ${gateway}`);
          return response;
        } else {
          console.warn(`[IPFSService] Gateway ${gateway} returned status ${response.status}`);
          lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.warn(`[IPFSService] Gateway ${gateway} timed out after ${this.timeout}ms`);
          lastError = new Error(`Timeout after ${this.timeout}ms`);
        } else {
          console.warn(`[IPFSService] Gateway ${gateway} failed:`, error.message);
          lastError = error;
        }

        // Continue to next gateway
        continue;
      }
    }

    // All gateways failed
    const errorMsg = `All ${this.gateways.length} IPFS gateways failed for CID: ${cid}`;
    console.error(`[IPFSService] ${errorMsg}. Last error:`, lastError);
    throw new Error(`${errorMsg}. Last error: ${lastError?.message || 'Unknown error'}`);
  }

  /**
   * Fetch and parse JSON from IPFS
   * @param {string} uri - IPFS URI or CID
   * @returns {Promise<object>} - Parsed JSON object
   */
  async fetchJSON(uri) {
    try {
      const response = await this.fetchFromIPFS(uri);
      const data = await response.json();
      console.log('[IPFSService] Successfully fetched and parsed JSON');
      return data;
    } catch (error) {
      console.error('[IPFSService] Error fetching JSON:', error);
      throw new Error(`Failed to fetch JSON from IPFS: ${error.message}`);
    }
  }

  /**
   * Fetch text content from IPFS
   * @param {string} uri - IPFS URI or CID
   * @returns {Promise<string>} - Text content
   */
  async fetchText(uri) {
    try {
      const response = await this.fetchFromIPFS(uri);
      const text = await response.text();
      console.log('[IPFSService] Successfully fetched text');
      return text;
    } catch (error) {
      console.error('[IPFSService] Error fetching text:', error);
      throw new Error(`Failed to fetch text from IPFS: ${error.message}`);
    }
  }

  /**
   * Get a gateway URL for a given CID
   * @param {string} cid - IPFS CID or URI
   * @param {number} gatewayIndex - Index of gateway to use (default: 0 for primary)
   * @returns {string} - Full gateway URL
   */
  getGatewayUrl(cid, gatewayIndex = 0) {
    const cleanCID = this.extractCID(cid);
    const gateway = this.gateways[gatewayIndex] || this.gateways[0];
    return `${gateway}${cleanCID}`;
  }

  /**
   * Get the primary (preferred) gateway URL
   * @param {string} cid - IPFS CID or URI
   * @returns {string} - Full gateway URL using primary gateway
   */
  getPrimaryGatewayUrl(cid) {
    return this.getGatewayUrl(cid, 0);
  }

  /**
   * Test all gateways and return their status
   * @param {string} testCID - CID to test with (optional)
   * @returns {Promise<Array>} - Array of gateway test results
   */
  async testGateways(testCID = 'QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG') {
    console.log('[IPFSService] Testing all gateways...');

    const results = await Promise.allSettled(
      this.gateways.map(async (gateway, index) => {
        const startTime = Date.now();
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), this.timeout);

          const response = await fetch(`${gateway}${testCID}`, {
            signal: controller.signal,
            method: 'HEAD' // Use HEAD to avoid downloading content
          });

          clearTimeout(timeoutId);
          const duration = Date.now() - startTime;

          return {
            gateway,
            index,
            status: response.ok ? 'success' : 'error',
            statusCode: response.status,
            duration: `${duration}ms`,
            ok: response.ok
          };
        } catch (error) {
          const duration = Date.now() - startTime;
          return {
            gateway,
            index,
            status: 'failed',
            error: error.message,
            duration: `${duration}ms`,
            ok: false
          };
        }
      })
    );

    const gatewayResults = results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        return {
          gateway: this.gateways[index],
          index,
          status: 'failed',
          error: result.reason?.message || 'Unknown error',
          ok: false
        };
      }
    });

    console.log('[IPFSService] Gateway test results:', gatewayResults);
    return gatewayResults;
  }

  /**
   * Get the number of configured gateways
   * @returns {number} - Number of gateways
   */
  getGatewayCount() {
    return this.gateways.length;
  }

  /**
   * Add a custom gateway to the list
   * @param {string} gatewayUrl - Gateway URL (should end with /ipfs/)
   * @param {number} priority - Position in gateway list (0 = highest priority)
   */
  addGateway(gatewayUrl, priority = this.gateways.length) {
    // Ensure URL ends with /ipfs/
    const formattedUrl = gatewayUrl.endsWith('/ipfs/')
      ? gatewayUrl
      : gatewayUrl.endsWith('/')
        ? `${gatewayUrl}ipfs/`
        : `${gatewayUrl}/ipfs/`;

    this.gateways.splice(priority, 0, formattedUrl);
    console.log(`[IPFSService] Added gateway at priority ${priority}: ${formattedUrl}`);
  }

  /**
   * Set custom timeout for gateway requests
   * @param {number} ms - Timeout in milliseconds
   */
  setTimeout(ms) {
    this.timeout = ms;
    console.log(`[IPFSService] Timeout set to ${ms}ms`);
  }
}

// Export singleton instance
const ipfsService = new IPFSService();

export default ipfsService;
export { IPFSService };
